import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";
import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
var _VisibilityOffIcon, _VisibilityIcon, _ClearIcon;
var _excluded = ["onChange"],
  _excluded2 = ["id", "className", "label", "error", "helperText", "variant", "type", "disabled", "startAdornment", "endAdornment", "placeholder", "inputProps", "InputProps", "isClearable", "handleClearClick", "allowNegative", "allowLeadingZeros", "format", "mask", "prefix", "suffix", "decimalSeparator", "thousandSeparator", "onBlur", "onPlacesBlur", "value"];
/* eslint-disable no-unused-expressions */
import React, { useState, forwardRef } from 'react';
import PropTypes from 'prop-types';
import NumberFormat from 'react-number-format';
import { TextField as MuiTextField, IconButton as MuiIconButton, InputAdornment as MuiInputAdornment } from '@mui/material';
import { Clear as ClearIcon, Error as ErrorIcon, Visibility as VisibilityIcon, VisibilityOff as VisibilityOffIcon } from '@mui/icons-material';
import { Logger } from 'wtb-utils';
import { formatId } from '../../utils';
import Typography from '../Typography';
import useStyles from './Input.styles';
var NumberInput = /*#__PURE__*/forwardRef(function (props, ref) {
  var onChange = props.onChange,
    other = _objectWithoutProperties(props, _excluded);
  return /*#__PURE__*/React.createElement(NumberFormat, _extends({}, other, {
    allowEmptyFormatting: true,
    getInputRef: ref,
    onValueChange: function onValueChange(values) {
      onChange({
        target: _extends({
          name: props.name
        }, values)
      });
    }
  }));
});
var Input = /*#__PURE__*/forwardRef(function (props, ref) {
  var _Typography, _Typography2, _MuiInputAdornment;
  var id = props.id,
    className = props.className,
    label = props.label,
    error = props.error,
    helperText = props.helperText,
    variant = props.variant,
    type = props.type,
    disabled = props.disabled,
    startAdornment = props.startAdornment,
    endAdornment = props.endAdornment,
    placeholder = props.placeholder,
    inputProps = props.inputProps,
    InputProps = props.InputProps,
    _props$isClearable = props.isClearable,
    isClearable = _props$isClearable === void 0 ? true : _props$isClearable,
    handleClearClick = props.handleClearClick,
    allowNegative = props.allowNegative,
    allowLeadingZeros = props.allowLeadingZeros,
    format = props.format,
    mask = props.mask,
    prefix = props.prefix,
    suffix = props.suffix,
    decimalSeparator = props.decimalSeparator,
    thousandSeparator = props.thousandSeparator,
    _onBlur = props.onBlur,
    onPlacesBlur = props.onPlacesBlur,
    value = props.value,
    other = _objectWithoutProperties(props, _excluded2);
  var _useStyles = useStyles(),
    classes = _useStyles.classes,
    cx = _useStyles.cx;
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    showPasswordIcon = _useState2[0],
    setShowPasswordIcon = _useState2[1];
  var _useState3 = useState(type !== 'number' ? type : null),
    _useState4 = _slicedToArray(_useState3, 2),
    inputType = _useState4[0],
    setInputType = _useState4[1];
  var getId = function getId() {
    if (id) return formatId(id);else return formatId(label);
  };
  var hasError = function hasError() {
    if (typeof error === 'string' && error !== '' || error === 1) return true;else if (typeof error === 'boolean') return error;else if (error === null || error === undefined || error === 0 || error === '') return false;else {
      Logger.error("Wrong param '".concat(error, "' provided to Input component."));
      return false;
    }
  };
  var getHelperText = function getHelperText() {
    if (error && typeof error === 'string') {
      if (helperText) {
        if (typeof helperText === 'string') {
          return _Typography || (_Typography = /*#__PURE__*/React.createElement(Typography, null, error, " - ", /*#__PURE__*/React.createElement("em", null, helperText)));
        } else {
          return _Typography2 || (_Typography2 = /*#__PURE__*/React.createElement(Typography, null, error, " - ", helperText));
        }
      } else {
        return error;
      }
    } else {
      return helperText;
    }
  };
  var handleShowPassword = function handleShowPassword() {
    setInputType(inputType === 'password' ? 'text' : 'password');
    setShowPasswordIcon(inputType === 'password');
  };
  var getEndAdornment = function getEndAdornment() {
    if (type === 'password') {
      return /*#__PURE__*/React.createElement(MuiIconButton, {
        position: "end",
        disabled: disabled,
        onClick: handleShowPassword
      }, /*#__PURE__*/React.createElement(MuiInputAdornment, {
        position: "end",
        "aria-label": "Toggle password visibility"
      }, showPasswordIcon ? _VisibilityOffIcon || (_VisibilityOffIcon = /*#__PURE__*/React.createElement(VisibilityOffIcon, null)) : _VisibilityIcon || (_VisibilityIcon = /*#__PURE__*/React.createElement(VisibilityIcon, null))));
    }
    if (InputProps.endAdornment) {
      return _extends({}, InputProps.endAdornment, {
        props: _extends({}, InputProps.endAdornment.props, {
          className: cx(InputProps.endAdornment.props.className, _defineProperty({}, classes.errorIcon, hasError()))
        })
      });
    }
    if (endAdornment) {
      return /*#__PURE__*/React.createElement(MuiInputAdornment, {
        className: classes.errorIcon,
        position: "end"
      }, endAdornment);
    }
    if (hasError()) {
      return /*#__PURE__*/React.createElement(MuiInputAdornment, {
        position: "end",
        "aria-label": "Error"
      }, /*#__PURE__*/React.createElement(ErrorIcon, {
        className: classes.errorIcon
      }));
    }
    if (isClearable) {
      return /*#__PURE__*/React.createElement(MuiInputAdornment, {
        position: "end",
        "aria-label": "Clear value"
      }, /*#__PURE__*/React.createElement(MuiIconButton, {
        position: "end",
        disableRipple: true,
        id: "input-clear-icon",
        onClick: function onClick() {
          props.onChange({
            target: {
              name: props.name,
              value: ''
            }
          });
          // eslint-disable-next-line no-unused-expressions
          handleClearClick && handleClearClick();
          if (ref && ref.current) {
            // eslint-disable-next-line no-param-reassign
            ref.current.value = '';
            ref.current.focus();
          }
        },
        tabIndex: -1
      }, _ClearIcon || (_ClearIcon = /*#__PURE__*/React.createElement(ClearIcon, null))));
    }
    return undefined;
  };
  var getStartAdornment = function getStartAdornment() {
    if (startAdornment) {
      return _MuiInputAdornment || (_MuiInputAdornment = /*#__PURE__*/React.createElement(MuiInputAdornment, {
        position: "start"
      }, startAdornment));
    } else {
      return undefined;
    }
  };
  return /*#__PURE__*/React.createElement(MuiTextField, _extends({
    id: getId(),
    className: cx(classes.root, className, _defineProperty({}, classes.disabled, other.disabled)),
    type: inputType,
    disabled: disabled,
    label: label,
    inputRef: ref,
    error: hasError(),
    value: value,
    helperText: getHelperText(),
    variant: variant,
    placeholder: type !== 'number' ? placeholder : undefined,
    InputProps: _extends({}, InputProps, {
      inputComponent: type === 'number' ? NumberInput : undefined,
      endAdornment: getEndAdornment(),
      startAdornment: getStartAdornment()
    })
    // eslint-disable-next-line react/jsx-no-duplicate-props
    ,
    inputProps: _extends({}, inputProps, type === 'number' && {
      allowNegative: allowNegative,
      allowLeadingZeros: allowLeadingZeros,
      format: format,
      mask: mask,
      placeholder: placeholder,
      decimalSeparator: decimalSeparator,
      thousandSeparator: thousandSeparator
    }),
    onBlur: function onBlur(e) {
      _onBlur && _onBlur(e);
      onPlacesBlur && onPlacesBlur(e);
    }
  }, other));
});
process.env.NODE_ENV !== "production" ? Input.propTypes = {
  id: PropTypes.string,
  autoFormat: PropTypes.bool,
  inputActiveColor: PropTypes.string,
  InputProps: PropTypes.object,
  inputProps: PropTypes.object,
  disabled: PropTypes.bool,
  isClearable: PropTypes.bool,
  handleClearClick: PropTypes.func,
  helperText: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  inputClass: PropTypes.string,
  label: PropTypes.string,
  margin: PropTypes.oneOf(['none', 'dense', 'normal']),
  multiline: PropTypes.bool,
  onBlur: PropTypes.func,
  onChange: PropTypes.func,
  onFocus: PropTypes.func,
  minRows: PropTypes.number,
  maxRows: PropTypes.number,
  size: PropTypes.oneOf(['small', 'medium']),
  type: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.object, PropTypes.number, PropTypes.string]),
  variant: PropTypes.oneOf(['standard', 'outlined', 'filled'])
} : void 0;
Input.defaultProps = {
  size: 'small',
  type: 'text',
  variant: 'outlined',
  InputProps: {},
  inputProps: {},
  isClearable: true
};
export default Input;